import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import angelEye from '../images/fallen_angel_eye.jpg';
import angelFull from '../images/fallen_angel_full.jpg';

/* ─── Tension Matrix ─────────────────────────────────────────────────────────
   Each row is a pair of apparent opposites that coexist inside the same person.
   Leave a placeholder comment where you want to fill in your own framing later.
────────────────────────────────────────────────────────────────────────────── */
const TENSIONS = [
  {
    left: 'Rigour',
    right: 'Chaos',
    note: 'I need a solid system before I can comfortably blow it up.',
  },
  {
    left: 'Deeply Italian',
    right: 'Restless wanderer',
    note: 'Attached to roots; allergic to staying still.',
  },
  {
    left: 'Engineer',
    right: 'Humanist',
    note: 'Numbers explain the world. Literature makes it worth living.',
  },
  {
    left: 'Introvert',
    right: 'Connector',
    note: 'I recharge alone but come alive in the right conversation.',
  },
  {
    left: 'Long-term thinker',
    right: 'Impatient executor',
    note: 'Ten-year vision, zero tolerance for slow Mondays.',
  },
  {
    left: 'Skeptic',
    right: 'Optimist',
    note: 'I question everything and still bet on humans getting it right.',
  },
];

/* ─── People Who Shaped Me ───────────────────────────────────────────────────
   A curated list of people — real and fictional — who left a mark.
   Add a short note on *what* they gave you, not just who they are.
   [ placeholder entries marked with ★ — replace with your own ]
────────────────────────────────────────────────────────────────────────────── */
const SHAPERS = [
  {
    name: 'My parents',
    role: 'First teachers',
    note: 'They gave me curiosity before they gave me anything else. Everything else is built on that.',
    category: 'family',
  },
  {
    name: 'Dante Alighieri',
    role: 'Poet & moral compass',
    note: '"Fatti non foste a viver come bruti" — the sentence I return to when I lose direction.',
    category: 'literary',
  },
  {
    name: 'Luigi Pirandello',
    role: 'Novelist & philosopher',
    note: 'Taught me that identity is fluid, not fixed. One, no one, and one hundred thousand.',
    category: 'literary',
  },
  {
    name: 'Italo Calvino',
    role: 'Novelist & dreamer',
    note: 'Proved that precision and imagination are not opposites — they are the same discipline.',
    category: 'literary',
  },
  {
    name: '★ A mentor (placeholder)',
    role: 'Professional guide',
    note: 'Replace this entry with the person who first took your career seriously and showed you what good looks like.',
    category: 'professional',
    placeholder: true,
  },
  {
    name: '★ A friend (placeholder)',
    role: 'Mirror',
    note: 'Replace this with the friend who tells you the truth when everyone else says what you want to hear.',
    category: 'personal',
    placeholder: true,
  },
  {
    name: 'Richard Feynman',
    role: 'Physicist & teacher',
    note: 'Proved that understanding something deeply enough means you can explain it to anyone.',
    category: 'intellectual',
  },
  {
    name: 'Albert Camus',
    role: 'Philosopher of the absurd',
    note: '"One must imagine Sisyphus happy." The sentence that reframes every setback.',
    category: 'literary',
  },
];

const CATEGORY_COLOR = {
  family:       'var(--teal)',
  literary:     'var(--gold)',
  professional: '#8a9fd4',
  personal:     '#c47b8a',
  intellectual: '#a07bc4',
};

export default function ThePerson() {
  const [angelOverlay, setAngelOverlay] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setAngelOverlay(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div style={{ padding: '48px', maxWidth: '860px' }}>

      {/* ── Hero: header left, eye right ── */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '40px',
        marginBottom: '48px',
        flexWrap: 'wrap',
      }}>
        {/* Left: eyebrow + title + subtitle */}
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div className="eyebrow" style={{ marginBottom: '12px' }}>02 — The Person</div>
          <h1 className="page-title" style={{ fontSize: 'clamp(2.8rem, 6vw, 5rem)', margin: '0 0 16px' }}>
            Massimo
          </h1>
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '1.15rem',
            color: 'var(--text-2)',
            margin: 0,
            lineHeight: 1.5,
          }}>
            Uno, Nessuno e Centomila
          </p>
        </div>

        {/* Right: angel eye — click to open full painting */}
        <div style={{ flexShrink: 0 }}>
          <img
            src={angelEye}
            alt=""
            draggable={false}
            onClick={() => setAngelOverlay(true)}
            style={{
              width: '220px',
              borderRadius: '5px',
              display: 'block',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          />
        </div>
      </div>

      {/* ── Full painting overlay (lightbox) — portal to escape containing-block transforms ── */}
      {ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            background: '#000',
            opacity: angelOverlay ? 1 : 0,
            pointerEvents: angelOverlay ? 'all' : 'none',
            transition: 'opacity 0.4s ease',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setAngelOverlay(false)}
        >
          <img
            src={angelFull}
            alt="L'angelo caduto"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
              maxWidth: '90vw',
              objectFit: 'contain',
              display: 'block',
              opacity: 0.92,
            }}
          />
          <p style={{
            position: 'absolute',
            bottom: '20px',
            right: '24px',
            margin: 0,
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: '0.03em',
          }}>
            L'angelo caduto — William-Adolphe Bouguereau, 1847
          </p>
          <button
            onClick={() => setAngelOverlay(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '24px',
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.3)',
              fontSize: '1.3rem',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '8px',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}

      {/* ── Main prose ── */}
      <div className="prose-detail">
        <p>
          <b>**AI Generated, for now... and nonetheless, it's not far from reality - with some mistakes</b> <i>(me, an introvert? lol)</i><br />
          In Luigi Pirandello's masterpiece <em>Uno, Nessuno e Centomila</em>, the protagonist discovers
          that he is simultaneously one person (to himself), no one (since identity is an illusion), and one
          hundred thousand people (as seen differently by everyone around him). I find this the most honest
          framework for trying to describe a human being.
        </p>

        <p>
          So how do I see Massimo? I am an engineer by training, a thinker by nature, and a wanderer by
          choice. I grew up in Desenzano del Garda — a small town on the shores of Lake Garda in northern
          Italy — where the mountains meet the water and the pace of life gives you space to ask big
          questions. I spent my youth chasing mathematics competitions, reading philosophy, and arguing
          about football. Not much has changed.
        </p>

        <p>
          I believe that curiosity is the only non-negotiable human virtue. The Dante quote on my home page
          is not just decoration — it is the sentence I return to whenever I wonder whether something is
          worth pursuing. <em>You were not made to live like brutes, but to pursue virtue and knowledge.</em>{' '}
          That is the whole programme.
        </p>

        <p>
          I am contradictory in the ways that matter most. I love structure and I love chaos. I am deeply
          Italian in my attachment to food, family, and beauty, and deeply un-Italian in my restlessness and
          my need to keep moving. I find comfort in rigour and excitement in ambiguity. I am at my best
          when a problem has no clean solution and someone needs to figure one out anyway.
        </p>

        <p>
          I moved to the Netherlands in 2022 for work, which turned out to be one of the better decisions
          of my life — not because the Netherlands is objectively better than Italy (it isn't; the food is a
          crime), but because living somewhere foreign strips away your defaults and forces you to choose who
          you want to be, deliberately, every single day.
        </p>

        <p>
          The rest of the story is in the other pages of this website. The projects tell you what I have
          built. The travel stories tell you where I have been and what I noticed. The career section tells
          you how I got here. Together, they are a more honest portrait than anything I could write on this
          page alone.
        </p>
      </div>

      {/* ── Tension Matrix ── */}
      <section style={{ marginTop: '64px' }}>
        <div className="eyebrow" style={{ marginBottom: '8px' }}>Tension Matrix</div>
        <h2 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: '2rem',
          fontWeight: 700,
          color: 'var(--text)',
          margin: '0 0 8px',
          letterSpacing: '-0.02em',
        }}>
          The contradictions I live with
        </h2>
        <p style={{
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontSize: '0.85rem',
          color: 'var(--text-3)',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}>
          Identity is not a clean set of traits — it is a set of tensions held in balance.
          These are mine.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '12px',
        }}>
          {TENSIONS.map((t, i) => (
            <div
              key={i}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '20px 24px',
              }}
            >
              {/* Left ↔ Right */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '10px',
              }}>
                <span style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: 'var(--gold)',
                }}>
                  {t.left}
                </span>
                <span style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.65rem',
                  color: 'var(--text-3)',
                  flexShrink: 0,
                }}>
                  ↔
                </span>
                <span style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: 'var(--teal)',
                }}>
                  {t.right}
                </span>
              </div>
              <p style={{
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontSize: '0.78rem',
                color: 'var(--text-3)',
                margin: 0,
                lineHeight: 1.55,
                fontStyle: 'italic',
              }}>
                {t.note}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── People Who Shaped Me ── */}
      <section style={{ marginTop: '64px' }}>
        <div className="eyebrow" style={{ marginBottom: '8px' }}>People Who Shaped Me</div>
        <h2 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: '2rem',
          fontWeight: 700,
          color: 'var(--text)',
          margin: '0 0 8px',
          letterSpacing: '-0.02em',
        }}>
          The ones who left a mark
        </h2>
        <p style={{
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontSize: '0.85rem',
          color: 'var(--text-3)',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}>
          A curated, partial, and evolving list of people — real and fictional — who contributed
          something irreversible to who I am. The placeholder entries are honest invitations to keep filling
          this in.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {SHAPERS.map((person, i) => (
            <div
              key={i}
              style={{
                background: person.placeholder ? 'transparent' : 'var(--surface)',
                border: `1px solid ${person.placeholder ? 'var(--border-2)' : 'var(--border)'}`,
                borderLeft: `3px solid ${CATEGORY_COLOR[person.category] || 'var(--border)'}`,
                borderRadius: '6px',
                padding: '16px 20px',
                opacity: person.placeholder ? 0.55 : 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <span style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: 'var(--text)',
                }}>
                  {person.name}
                </span>
                <span style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.6rem',
                  letterSpacing: '0.06em',
                  color: CATEGORY_COLOR[person.category] || 'var(--text-3)',
                  textTransform: 'uppercase',
                }}>
                  {person.role}
                </span>
              </div>
              <p style={{
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                fontSize: '0.8rem',
                color: 'var(--text-3)',
                margin: 0,
                lineHeight: 1.55,
                fontStyle: person.placeholder ? 'italic' : 'normal',
              }}>
                {person.note}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer note */}
      <p style={{ color: 'var(--text-3)', fontStyle: 'italic', fontSize: '1rem', marginTop: '48px' }}>
        — This page will keep evolving. Identity is not a fixed point.
      </p>
    </div>
  );
}
